import { FC, Fragment, useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import cn from 'classnames';

import { calculateTooltipPostion } from '@helpers/calculateTooltipPostion';

import './Tooltip.scss';

const defaultCoords = { top: 0, left: -1000 };

type TTooltip = {
  content: any;
};

const Tooltip: FC<TTooltip> = ({ children, content }) => {
  const [container, setContainer] = useState<any>(null);

  const [coords, setCoords] = useState(defaultCoords);
  const [isHoveredTooltip, setIsHoveredTooltip] = useState(false);
  const [isHoveredBase, setIsHoveredBase] = useState(false);
  const [isOpened, setIsOpened] = useState(true);
  const tooltipRef = useRef(null);
  const wrapperRef: any = useRef(null);

  const getCoords = useCallback(() => {
    const {
      left,
      top,
      bottom,
      width: blockWidth,
    } = wrapperRef.current.getBoundingClientRect();

    const newCoords = calculateTooltipPostion(
      left,
      top,
      bottom,
      blockWidth,
      tooltipRef,
    );

    return newCoords;
  }, []);

  const handleVisibility = useCallback((newIsOpened: boolean) => {
    setIsOpened(newIsOpened);
  }, []);

  const handleMouseOver = useCallback(() => {
    const newCoords = getCoords();

    setCoords(newCoords);
    handleVisibility(true);
    setIsHoveredBase(true);
  }, [getCoords, handleVisibility]);

  const handleMouseLeave = useCallback(() => {
    setIsHoveredBase(false);
    handleVisibility(false);
  }, [handleVisibility]);

  const handleTooltipMouseOver = useCallback(() => {
    setIsHoveredTooltip(true);
  }, []);

  const handleTooltipMouseLeave = useCallback(() => {
    setIsHoveredTooltip(false);
  }, []);

  useEffect(() => {
    if (!isHoveredBase && !isHoveredTooltip) {
      setCoords(defaultCoords);
    }
  }, [isHoveredBase, isHoveredTooltip]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setContainer(document.body);
    }
  }, []);

  useEffect(() => {
    if (isHoveredBase) {
      const newCoords = getCoords();
      setCoords(newCoords);
    }
  }, [content, isHoveredBase, getCoords]);

  if (!container) return null;

  return (
    <Fragment>
      <div
        ref={wrapperRef}
        className="tooltip"
        onMouseEnter={handleMouseOver}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>
      {createPortal(
        <div
          className={cn('popup', {
            active: isOpened,
          })}
          style={coords}
          ref={tooltipRef}
          onMouseEnter={handleTooltipMouseOver}
          onMouseLeave={handleTooltipMouseLeave}
        >
          {content}
        </div>,
        document.body,
      )}
    </Fragment>
  );
};

export default Tooltip;
